import React, { useState } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import Text from "@components/CustomText";
import color from "@constants/color";
import ChevronRight from "@assets/svg/ChevronRight";
import Tooltip from "react-native-walkthrough-tooltip";
import TooltipInfo from "@assets/svg/Wallet/Tooltip";
import Info from "@assets/svg/Wallet/Info";
import FailedInfo from "@assets/svg/Wallet/FailedInfo";

interface MainTransactionCardProps {
  disableCta: boolean;
  item: any;
  onPress: (
    nav_to: string,
    cta_type: string,
    cta_api: string,
    item: object,
  ) => void;
}

const MainTransactionCard: React.FC<MainTransactionCardProps> = ({
  item,
  disableCta,
  onPress,
}) => {
  const {
    date,
    subtitle_lines,
    value_txt,
    status_txt,
    status,
    cta_txt,
    cta_type,
    cta_api,
    nav_to,
    cta_info,
    status_color,
    failure_reason,
  } = item;

  const [toolTipVisible, setToolTipVisible] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "green":
        return color.plain_green;
      case "yellow":
        return color.mediumBlack;
      case "red":
        return color.plain_red;
      default:
        return color.mediumBlack;
    }
  };

  const renderTooltip = (extra_info: any) => {
    return (
      <View>
        {extra_info.map((i: any) => (
          <Text variant="caption_3">
            {i.title} - {i.value}
          </Text>
        ))}
      </View>
    );
  };

  return (
    <View style={styles.cardContainer}>
      <View style={styles.row}>
        <View style={styles.leftColumn}>
          <View>
            <Text variant="caption_4">{date}</Text>
            {Array.isArray(subtitle_lines) ? (
              subtitle_lines.map((i, index) => (
                <View
                  key={index}
                  style={[styles.row, { alignItems: "center" }]}
                >
                  <Text
                    key={index}
                    style={styles.subtitleTxt}
                    variant="caption_3"
                  >
                    {i.value}
                  </Text>
                  {i.extra_info && (
                    <Tooltip
                      isVisible={toolTipVisible}
                      content={renderTooltip(i.extra_info)}
                      placement="bottom"
                      backgroundColor="rgba(0,0,0,0.1)"
                      contentStyle={{ flex: 1, borderRadius: 10, padding: 10 }}
                      onClose={() => setToolTipVisible(false)}
                    >
                      <TouchableOpacity
                        onPress={() => setToolTipVisible(true)}
                        style={{ marginLeft: 4 }}
                      >
                        <TooltipInfo />
                      </TouchableOpacity>
                    </Tooltip>
                  )}
                </View>
              ))
            ) : (
              <Text variant="caption_3" style={styles.subtitleTxt}>
                {subtitle_lines}
              </Text>
            )}
          </View>
          {Array.isArray(cta_info) && cta_info.length > 0 && (
            <View style={styles.ctaInfo}>
              <Info />
              {cta_info.map((i, index) => (
                <Text
                  key={index}
                  variant="caption_3"
                  style={styles.ctaInfoText}
                >
                  {i}
                </Text>
              ))}
            </View>
          )}
          {failure_reason && (
            <View style={styles.failedReason}>
              <FailedInfo />
              <Text variant="caption_3" style={styles.failedReasonText}>
                {failure_reason}
              </Text>
            </View>
          )}
        </View>
        <View style={styles.rightColumnContent}>
          <View style={styles.amountContainer}>
            <Text
              variant="body_text_4"
              style={{
                color: getStatusColor(status_color),
                textDecorationLine:
                  status === "errored" || status === "cancelled"
                    ? "line-through"
                    : "none",
              }}
            >
              {value_txt}
            </Text>
            <Text
              variant="caption_2"
              style={[
                styles.statusTxt,
                { color: status_color === "yellow" ? color.pendingText : null },
              ]}
            >
              {status_txt}
            </Text>
          </View>
          {!disableCta && cta_txt ? (
            <TouchableOpacity
              style={styles.ctaContainer}
              onPress={() => onPress(nav_to, cta_type, cta_api, item)}
              testID={`${cta_txt}`}
            >
              <Text variant="caption_2" style={styles.cta}>
                {cta_txt}
              </Text>

              <ChevronRight
                pathFill={color.textBlue}
                style={styles.chevronIcon}
              />
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 16,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: "#ffffff",
    shadowRadius: 8,
    elevation: 1,
  },
  row: {
    flexDirection: "row",
  },
  title: {
    fontWeight: "bold",
  },
  leftColumn: {
    flex: 2,
    justifyContent: "space-between",
  },
  rightColumnContent: {
    flex: 1,
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  amountContainer: {
    alignItems: "flex-end",
  },
  ctaContainer: {
    marginTop: 10,
    flexDirection: "row",
    alignItems: "center",
  },
  subtitleTxt: {
    color: color.mediumBlacklightBlack,
    marginTop: 2,
    fontSize: 11,
    lineHeight: 15,
  },
  statusTxt: {
    marginTop: 2,
  },
  cta: {
    color: color.textBlue,
    textDecorationLine: "underline",
  },
  chevronIcon: {
    height: 10,
    width: 10,
    left: 4,
  },
  ctaInfo: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  ctaInfoText: {
    color: color.textBlue,
    marginLeft: 4,
  },
  failedReason: {
    marginTop: 4,
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  failedReasonText: {
    color: color.mediumBlack,
    marginLeft: 4,
  },
});

export default MainTransactionCard;
